import { useState, useEffect } from 'react';
import { FileText, Building, Languages, FileCheck, ArrowLeft, ArrowRight, Crown, Check, Zap, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Services() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    service_type: 'residency',
    title: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  const services = [
    {
      type: 'translation',
      title: isAr ? 'خدمات الترجمة القانونية' : 'Legal Translation Services',
      icon: Languages,
      description: isAr 
        ? 'خدمات ترجمة المستندات القانونية المعتمدة من قبل محترفين ذوي خبرة'
        : 'Certified legal document translation services by experienced professionals',
      gradient: 'from-blue-500 to-cyan-500',
      link: '/services/translation'
    },
    {
      type: 'document_clearing',
      title: isAr ? 'الطباعة وتخليص المستندات' : 'Typing & Document Clearing',
      icon: FileText,
      description: isAr 
        ? 'خدمات طباعة وتخليص مستندات سريعة وموثوقة للدوائر الحكومية'
        : 'Fast and reliable document typing and clearing services',
      gradient: 'from-orange-500 to-amber-500',
      link: '/services/documents'
    },
    {
      type: 'company_formation',
      title: isAr ? 'تأسيس الشركات' : 'Start Your Company',
      icon: Building,
      description: isAr 
        ? 'حلول شاملة لإنشاء الشركات تشمل التسجيل والترخيص وخدمات PRO'
        : 'Complete business setup solutions including registration and licensing',
      gradient: 'from-emerald-500 to-teal-500',
      link: '/services/company-formation'
    },
    {
      type: 'bill_payment',
      title: isAr ? 'دفع الفواتير' : 'Bill Payment',
      icon: CreditCard,
      description: isAr 
        ? 'خدمات دفع فواتير الكهرباء والماء والاتصالات وغيرها'
        : 'Pay your electricity, water, telecom and other bills',
      gradient: 'from-green-500 to-emerald-500',
      link: '/services/bill-payment'
    },
    {
      type: 'tax_agent',
      title: isAr ? 'خدمات الوكيل الضريبي' : 'Tax Agent Services',
      icon: FileCheck,
      description: isAr 
        ? 'استشارات ضريبية احترافية وخدمات تقديم الإقرارات'
        : 'Professional tax consultation and filing services',
      gradient: 'from-purple-500 to-pink-500',
      badge: isAr ? 'قريباً' : 'Coming Soon',
      link: null
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error(isAr ? 'يجب تسجيل الدخول أولاً' : 'Please login first');
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/services/request`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(isAr ? 'تم إرسال طلبك بنجاح' : 'Request submitted successfully!');
      setFormData({
        service_type: 'residency',
        title: '',
        description: '',
        contact_name: '',
        contact_email: '',
        contact_phone: ''
      });
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0A0B10]' : 'bg-[#F8FAFC]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Hero — Cyber-Luxe: dot grid + sky/gold orbs + brand scanline (dark & light) */}
      <section className="relative overflow-hidden pt-16 pb-24 sm:pt-20 sm:pb-28" data-testid="services-hero">
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-[#0A0B10]' : 'bg-gradient-to-b from-white to-[#F8FAFC]'}`} />
        <div className={`absolute inset-0 ${isDarkMode ? 'cgm-dot-grid opacity-40' : 'cgm-dot-grid-light opacity-50'} [mask-image:radial-gradient(ellipse_at_top,black_25%,transparent_75%)]`} />
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute -top-32 end-[10%] w-[480px] h-[480px] rounded-full blur-[130px] ${isDarkMode ? 'bg-[#1E9BD9]/20' : 'bg-[#1E9BD9]/15'}`} />
          <div className={`absolute -bottom-32 start-[8%] w-[380px] h-[380px] rounded-full blur-[120px] ${isDarkMode ? 'bg-[#F5A962]/15' : 'bg-[#F5A962]/20'}`} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center animate-fadeIn">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border mb-6 ${isDarkMode ? 'bg-[#1E9BD9]/10 border-[#1E9BD9]/30' : 'bg-white border-[#1E9BD9]/30 shadow-sm'}`}>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
            </span>
            <Zap className="h-3.5 w-3.5 text-[#F5A962]" />
            <span className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'cgm-brand-text' : 'text-[#0284C7]'}`}>{isAr ? 'خدمات احترافية يديرها مساعد ذكي' : 'Professional services run by a smart assistant'}</span>
          </div>
          <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 tracking-tight leading-[1.1] ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`} data-testid="services-title">
            {isAr ? 'خدماتنا الحكومية ' : 'Government & '}
            <span className="cgm-brand-text inline-block pb-1">{isAr ? 'والقانونية' : 'Legal Services'}</span>
          </h1>
          <p className={`text-base md:text-lg max-w-2xl mx-auto leading-relaxed ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
            {isAr
              ? 'باقة متكاملة من الخدمات لتوفير وقتك وجهدك — ننجزها بسرعة وأمان من مكان واحد.'
              : 'A complete bundle of services to save your time and effort — handled quickly and securely in one place.'}
          </p>

          {/* Stats ribbon */}
          <div className={`mt-10 grid grid-cols-3 max-w-2xl mx-auto rounded-2xl overflow-hidden relative ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light'} shadow-[0_20px_60px_rgba(30,155,217,0.12)]`} data-testid="services-stats">
            <div className="absolute inset-x-8 top-0 cgm-scanline" />
            {[
              { v: '2,400+', l: isAr ? 'طلب تم إنجازه' : 'Orders completed' },
              { v: '24/7',   l: isAr ? 'دعم فوري' : 'Instant support' },
              { v: '< 48h',  l: isAr ? 'متوسط الإنجاز' : 'Avg. turnaround' },
            ].map((s, i) => (
              <div key={i} className={`py-4 px-3 ${i !== 0 ? `border-s ${isDarkMode ? 'border-white/10' : 'border-slate-200'}` : ''}`}>
                <div className={`text-xl sm:text-2xl font-extrabold tabular-nums ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{s.v}</div>
                <div className={`text-[11px] sm:text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="absolute inset-x-0 bottom-0 cgm-scanline opacity-60" />
      </section>

      {/* Services Grid — balanced 3-col max for breathing room */}
      <section className="py-16 -mt-16 relative z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, idx) => (
              <div
                key={service.type}
                className={`group relative rounded-3xl p-7 flex flex-col overflow-hidden cgm-card-glow cgm-shimmer cgm-rise ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light'}`}
                data-testid={`service-${service.type}`}
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="absolute inset-x-8 top-0 cgm-scanline opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className={`pointer-events-none absolute -bottom-16 -end-16 w-44 h-44 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-br ${service.gradient}`} style={{ opacity: 0.18 }} />

                {service.badge && (
                  <div className="absolute top-5 end-5 bg-gradient-to-r from-[#F5A962] to-[#F59E0B] text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-wide shadow-lg shadow-[#F59E0B]/30">
                    {service.badge}
                  </div>
                )}

                <div className={`relative w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 shadow-lg transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3`}>
                  <service.icon className="h-8 w-8 text-white drop-shadow" />
                </div>

                <div className="relative flex-grow">
                  <h3 className={`text-lg font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    {service.title}
                  </h3>
                  <p className={`text-sm leading-relaxed mb-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{service.description}</p>
                </div>

                <div className="relative mt-auto">
                  {service.link ? (
                    <Link to={service.link}>
                      <Button className="w-full h-11 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white border-0 shadow-md shadow-[#1E9BD9]/25 hover:shadow-xl transition-all">
                        {isAr ? 'اكتشف المزيد' : 'Learn More'}
                        {isAr ? <ArrowLeft className="h-4 w-4 ms-2" /> : <ArrowRight className="h-4 w-4 ms-2" />}
                      </Button>
                    </Link>
                  ) : (
                    <Button disabled className={`w-full h-11 rounded-xl ${isDarkMode ? 'bg-white/5 text-slate-500 border border-white/5' : 'bg-slate-100 text-slate-400'} cursor-not-allowed`}>
                      {service.badge}
                    </Button>
                  )}
                </div>
                <div className="relative mt-5 h-[2px] w-12 rounded-full bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] transition-all duration-500 group-hover:w-24" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Packages Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`rounded-3xl p-8 md:p-14 relative overflow-hidden border ${isDarkMode ? 'bg-[#121520] border-[#1E9BD9]/20' : 'bg-gradient-to-br from-[#0F172A] via-[#121520] to-[#1E293B] border-[#1E9BD9]/30'}`}>
            {/* Decorative elements */}
            <div className="absolute inset-0 cgm-dot-grid opacity-30 [mask-image:radial-gradient(ellipse_at_center,black_20%,transparent_75%)]" />
            <div className="absolute -top-24 -end-24 w-96 h-96 bg-[#1E9BD9]/20 rounded-full blur-[100px]" />
            <div className="absolute -bottom-20 -start-10 w-72 h-72 bg-[#F5A962]/20 rounded-full blur-[100px]" />
            <div className="absolute inset-x-10 top-0 cgm-scanline" />

            <div className="relative z-10 text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#F59E0B]/20 to-[#F59E0B]/5 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-[#F59E0B]/30">
                <Crown className="h-4 w-4 text-[#F59E0B]" />
                {isAr ? 'باقات VIP لتخليص الخدمات الحكومية' : 'VIP Government Services Packages'}
              </div>

              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
                {isAr ? 'وفّر وقتك وجهدك معنا' : 'Save Your Time and Effort With Us'}
              </h2>

              <p className="text-gray-300 text-base md:text-lg mb-10 leading-relaxed max-w-2xl mx-auto">
                {isAr
                  ? 'هل سئمت من توظيف المناديب وصرف الرواتب ودفع رسوم التنقل؟ اشترك في باقاتنا واترك لنا جميع معاملاتك الحكومية.'
                  : 'Tired of hiring PROs, paying salaries, and transportation costs? Subscribe to our packages and leave all your government transactions to us.'}
              </p>

              <div className="flex flex-wrap items-center justify-center gap-4 mb-10">
                {[
                  isAr ? 'ابتداءً من 500 درهم/شهرياً' : 'Starting from 500 AED/month',
                  isAr ? 'بدون عقود طويلة' : 'No long contracts',
                  isAr ? 'مندوب مخصص' : 'Dedicated PRO',
                ].map((label, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-200 bg-white/5 border border-white/10 rounded-full px-4 py-2 backdrop-blur-sm">
                    <Check className="h-4 w-4 text-emerald-400" />
                    <span className="text-sm">{label}</span>
                  </div>
                ))}
              </div>

              <Link to="/service-packages" data-testid="services-packages-cta">
                <Button className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0EA5E9] hover:to-[#1E9BD9] text-white px-10 py-6 rounded-xl text-lg font-bold shadow-xl shadow-[#1E9BD9]/30 hover:shadow-[#1E9BD9]/50 transition-all hover:scale-[1.02]">
                  {isAr ? 'استعرض الباقات' : 'Browse packages'}
                  {isAr ? <ArrowLeft className="h-5 w-5 ms-2" /> : <ArrowRight className="h-5 w-5 ms-2" />}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other Requests Section — refined form */}
      <section className={`py-16 ${isDarkMode ? 'bg-[#0d0e14]' : 'bg-white'}`}>
        <div className="max-w-3xl mx-auto px-6">
          <div className={`rounded-3xl p-8 md:p-10 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark shadow-2xl shadow-black/40' : 'cgm-glass-light shadow-xl shadow-[#1E9BD9]/10'}`}>
            {/* subtle gradient accent at top */}
            <div className="absolute inset-x-8 top-0 cgm-scanline" />

            <div className="text-center mb-8">
              <div className={`inline-flex items-center gap-2 ${isDarkMode ? 'bg-white/5 text-gray-300 border-white/10' : 'bg-[#1E9BD9]/5 text-[#1E9BD9] border-[#1E9BD9]/20'} border px-4 py-1.5 rounded-full text-xs font-semibold mb-4`}>
                <FileText className="h-3.5 w-3.5" />
                {isAr ? 'طلب مخصص' : 'Custom request'}
              </div>
              <h2 className={`text-2xl md:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                {isAr ? 'طلبات أخرى' : 'Other Requests'}
              </h2>
              <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isAr ? 'هل لديك طلبات غير موجودة في خدماتنا؟ أخبرنا وسنساعدك' : 'Have requests not listed in our services? Tell us and we\'ll help'}
              </p>
            </div>

            {!user ? (
              <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-[#0a0a0a]' : 'bg-gray-50'}`}>
                <FileText className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-700' : 'text-gray-300'}`} />
                <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-[#0f172a]'}`}>
                  {isAr ? 'هل لديك طلبات غير موجودة؟' : 'Have requests not listed?'}
                </h3>
                <p className={`mb-6 max-w-md mx-auto ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {isAr 
                    ? 'سجّل دخولك لتتمكن من إرسال طلباتك الخاصة'
                    : 'Login to submit your special requests'}
                </p>
                <Link to="/login">
                  <Button className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white px-8">
                    {isAr ? 'تسجيل الدخول' : 'Login'}
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="service-request-form">
                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''} htmlFor="service_type">{t('services.serviceType')}</Label>
                  <Select
                    value={formData.service_type}
                    onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                  >
                    <SelectTrigger className={`mt-2 ${isDarkMode ? 'bg-[#0A0B10]/70 border-[#1E9BD9]/20 text-white' : ''}`} data-testid="service-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={isDarkMode ? 'bg-[#141414] border-white/10' : ''}>
                      <SelectItem value="translation">{isAr ? 'ترجمة قانونية' : 'Legal Translation'}</SelectItem>
                      <SelectItem value="document_clearing">{isAr ? 'طباعة وتخليص' : 'Document Clearing'}</SelectItem>
                      <SelectItem value="company_formation">{isAr ? 'تأسيس شركة' : 'Company Formation'}</SelectItem>
                      <SelectItem value="tax_agent">{isAr ? 'وكيل ضريبي' : 'Tax Agent'}</SelectItem>
                      <SelectItem value="residency">{isAr ? 'إقامة' : 'Residency'}</SelectItem>
                      <SelectItem value="other">{isAr ? 'أخرى' : 'Other'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''} htmlFor="title">{t('services.requestTitle')}</Label>
                  <Input
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className={`mt-2 ${isDarkMode ? 'bg-[#0A0B10]/70 border-[#1E9BD9]/20 text-white' : ''}`}
                    data-testid="service-title-input"
                  />
                </div>

                <div>
                  <Label className={isDarkMode ? 'text-gray-300' : ''} htmlFor="description">{t('services.details')}</Label>
                  <Textarea
                    id="description"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className={`mt-2 ${isDarkMode ? 'bg-[#0A0B10]/70 border-[#1E9BD9]/20 text-white' : ''}`}
                    data-testid="service-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''} htmlFor="contact_name">{t('services.name')}</Label>
                    <Input
                      id="contact_name"
                      required
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      className={`mt-2 ${isDarkMode ? 'bg-[#0A0B10]/70 border-[#1E9BD9]/20 text-white' : ''}`}
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''} htmlFor="contact_email">{t('common.email')}</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      required
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className={`mt-2 ${isDarkMode ? 'bg-[#0A0B10]/70 border-[#1E9BD9]/20 text-white' : ''}`}
                      data-testid="contact-email-input"
                    />
                  </div>

                  <div>
                    <Label className={isDarkMode ? 'text-gray-300' : ''} htmlFor="contact_phone">{t('common.phone')}</Label>
                    <Input
                      id="contact_phone"
                      type="tel"
                      required
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className={`mt-2 ${isDarkMode ? 'bg-[#0A0B10]/70 border-[#1E9BD9]/20 text-white' : ''}`}
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] hover:from-[#0284C7] hover:to-[#1E9BD9] text-white py-6 text-lg"
                  data-testid="submit-service-request"
                >
                  {loading ? (isAr ? 'جاري الإرسال...' : 'Submitting...') : (isAr ? 'إرسال الطلب' : 'Submit Request')}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
